import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const SingleCheckedAccordian = React.memo(
  ({
    summary,
    details,
    index,
    selectedAccordians,
    setSelectedAccordians,
    multiAccoridanData,
    setMultiAccordianData,
    isEditing,
    setIsEditing,
    editingValue,
    setEditingValue,
    currentPage,
    rowsPerPage,
    expandAll,
    forceExpandState,
    updateExpandedState,
    loaderCheck,
  }) => {
    const [expanded, setExpanded] = useState(null);

    // Effect to handle expandAll state changes from parent
    useEffect(() => {
      if (expandAll) {
        setExpanded(index);
        updateExpandedState(index, true);
      } else if (expandAll === false) {
        // Only collapse if explicitly set to false
        setExpanded(null);
        updateExpandedState(index, false);
      }
    }, [expandAll, index, updateExpandedState]);

    // Effect to handle page changes
    useEffect(() => {
      setExpanded(null);
      updateExpandedState(index, false);
    }, [currentPage, index, updateExpandedState]);

    // Effect to handle forced expand state changes (from Expand/Collapse All)
    useEffect(() => {
      if (forceExpandState !== undefined) {
        setExpanded(forceExpandState ? index : null);
        updateExpandedState(index, forceExpandState);
      }
    }, [forceExpandState, index, updateExpandedState]);

    useEffect(() => {
      if (isEditing === index) {
        setExpanded(index);
        updateExpandedState(index, true);
      }
    }, [isEditing, index, updateExpandedState]);

    const handleSelection = useCallback(
      (event, index) => {
        const updatedData = [...selectedAccordians];
        if (event.target.checked) {
          updatedData[index] = multiAccoridanData[index];
        } else {
          updatedData[index] = null;
        }
        setSelectedAccordians(updatedData);
      },
      [selectedAccordians, multiAccoridanData, setSelectedAccordians]
    );

    const handleEditSave = useCallback(
      (index) => {
        const updatedData = [...multiAccoridanData];
        const selectedUpdatedData = [...selectedAccordians];
        updatedData[index] = editingValue;
        if (selectedUpdatedData[index] !== null) {
          selectedUpdatedData[index] = editingValue;
        }
        setMultiAccordianData([...updatedData]);
        setSelectedAccordians([...selectedUpdatedData]);
        setIsEditing(null);
      },
      [
        multiAccoridanData,
        selectedAccordians,
        editingValue,
        setMultiAccordianData,
        setSelectedAccordians,
        setIsEditing,
      ]
    );

    const handleEditCancel = useCallback(() => {
      setIsEditing(null);
      setEditingValue({});
    }, [setIsEditing, setEditingValue]);

    const handleAccordionChange = (e, isExpanded) => {
      const newExpandedState = isExpanded ? index : null;
      setExpanded(newExpandedState);

      // Update the parent component about the expanded state change
      updateExpandedState(index, isExpanded);
    };

    const truncateByAlphabets = (text = "", limit) => {
      // keep only alphabets for counting
      const alphabetsOnly = text.replace(/[^a-zA-Z]/g, "");
      if (alphabetsOnly.length <= limit) return text;

      // Now truncate original text by walking through until we reach the limit
      let count = 0;
      let result = "";
      for (let char of text) {
        if (/[a-zA-Z]/.test(char)) count++;
        if (count > limit) break;
        result += char;
      }

      return result + "…";
    };

    return (
      <Accordion
        sx={{
          borderRadius: "0px !important",
          width: "100%",
          overflow: "hidden",
        }}
        expanded={expanded === index}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          sx={{
            height: "50px",
            backgroundColor: "#EAF1FF",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box width="34px">
              {expanded === index ? (
                <ExpandMoreIcon sx={{ marginRight: 1 }} />
              ) : (
                <ChevronRightIcon sx={{ marginRight: 1 }} />
              )}
            </Box>
            <Box>
              <Checkbox
                checked={
                  JSON.stringify(multiAccoridanData[index]) ===
                  JSON.stringify(selectedAccordians[index])
                }
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSelection(e, index);
                }}
                sx={{
                  "&.Mui-checked": {
                    color: "#49A3FF",
                  },
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "hidden", flexShrink: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  overflow: "hidden",
                  height: "20px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {truncateByAlphabets(summary, 90)}
              </Typography>
            </Box>

            <Box sx={{ marginLeft: "auto" }}>
              <IconButton
                className="edit-icon"
                onClick={() => {
                  setEditingValue(multiAccoridanData[index]);
                  setIsEditing(index);
                }}
                size="small"
                sx={{ p: 0.5 }}
                disabled={loaderCheck}
              >
                <PenIcon style={{ width: 18, height: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ paddingLeft: "40px" }}>
          {details}

          {isEditing === index && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                mt: 2,
              }}
            >
              <Button
                sx={{
                  color: "#49A3FF",
                  fontSize: "14px",
                  textTransform: "none",
                }}
                onClick={handleEditCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#49A3FF",
                  color: "white",
                  fontSize: "14px",
                  textTransform: "none",
                }}
                onClick={() => handleEditSave(index)}
              >
                Save
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);

const MultiAccordian = ({
  summaries,
  multiAccoridanDetails,
  selectedAccordians,
  setSelectedAccordians,
  multiAccoridanData,
  setMultiAccordianData,
  isEditing,
  setIsEditing,
  editingValue,
  setEditingValue,
  loaderCheck,
}) => {
  const [expandedStates, setExpandedStates] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [forceExpandState, setForceExpandState] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);

  // Initialize expanded states when component loads or data changes
  useEffect(() => {
    const initialStates = {};
    multiAccoridanDetails.forEach((_, index) => {
      initialStates[index] = false;
    });
    setExpandedStates(initialStates);
  }, [multiAccoridanDetails]);

  // Reset force expand state after it's applied
  useEffect(() => {
    if (forceExpandState !== undefined) {
      // Reset to undefined after a short delay to prevent continuous triggering
      const timer = setTimeout(() => {
        setForceExpandState(undefined);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [forceExpandState]);

  // Function to update expanded states from child components
  const updateExpandedState = useCallback((index, isExpanded) => {
    setExpandedStates((prev) => ({
      ...prev,
      [index]: isExpanded,
    }));
  }, []);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText("Content to be copied");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const selectedAll =
    JSON.stringify(selectedAccordians) === JSON.stringify(multiAccoridanData);

  const activeData = [];
  multiAccoridanDetails.forEach((eachAccordianData, index) => {
    if (!!eachAccordianData) {
      activeData.push({ [index]: eachAccordianData });
    }
  });

  const paginatedData = useMemo(() => {
    return activeData.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  }, [currentPage, activeData, rowsPerPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleUnselectAll = () => {
    setSelectedAccordians(selectedAccordians.map(() => null));
  };

  const handleSelectAll = () => {
    setSelectedAccordians([...multiAccoridanData]);
  };

  const handleToggleAll = () => {
    // Check if all visible accordions are expanded
    const visibleIndices = paginatedData.map((item) =>
      Number(Object.keys(item)[0])
    );
    const allExpanded = visibleIndices.every((index) => expandedStates[index]);

    // Set force expand state based on current state (true to expand all, false to collapse all)
    setForceExpandState(!allExpanded);

    // Update all visible accordions' expanded states
    const newStates = { ...expandedStates };
    visibleIndices.forEach((index) => {
      newStates[index] = !allExpanded;
    });
    setExpandedStates(newStates);
  };

  // Check if all visible accordions are expanded
  const isAllExpanded = useMemo(() => {
    if (paginatedData.length === 0) return false;

    const visibleIndices = paginatedData.map((item) =>
      Number(Object.keys(item)[0])
    );
    return visibleIndices.every((index) => expandedStates[index]);
  }, [paginatedData, expandedStates]);

  return (
    <Box width={"100%"}>
      <Box sx={{ height: "40px", width: "100%", paddingX: "10px" }}>
        {selectedAll ? (
          <Button
            sx={{ color: "#49A3FF", fontSize: "14px", textTransform: "none" }}
            onClick={handleUnselectAll}
          >
            Unselect all
          </Button>
        ) : (
          <Button
            sx={{ color: "#49A3FF", fontSize: "14px", textTransform: "none" }}
            onClick={handleSelectAll}
          >
            Select all
          </Button>
        )}
        <Button
          sx={{ color: "#49A3FF", fontSize: "14px", textTransform: "none" }}
          onClick={handleToggleAll}
        >
          {isAllExpanded ? "Collapse all" : "Expand all"}
        </Button>
      </Box>

      <Box
        sx={{
          overflowY: "scroll",
          "&::-webkit-scrollbar": { width: "0px" },
          scrollbarWidth: "none",
        }}
      >
        {paginatedData.map((eachPaginatedData) =>
          Object.entries(eachPaginatedData).map(
            ([index, eachAccordianDetails]) => (
              <Box key={`eachAccordian_${index}`}>
                <SingleCheckedAccordian
                  summary={summaries[index]}
                  details={eachAccordianDetails}
                  index={Number(index)}
                  selectedAccordians={selectedAccordians}
                  setSelectedAccordians={setSelectedAccordians}
                  multiAccoridanData={multiAccoridanData}
                  setMultiAccordianData={setMultiAccordianData}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  editingValue={editingValue}
                  setEditingValue={setEditingValue}
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  expandAll={expandAll}
                  forceExpandState={forceExpandState}
                  updateExpandedState={updateExpandedState}
                  loaderCheck={loaderCheck}
                />
              </Box>
            )
          )
        )}
      </Box>

      <Pagination
        count={Math.ceil(activeData.length / rowsPerPage)}
        page={currentPage}
        onChange={handlePageChange}
        sx={{ display: "flex", justifyContent: "center", mt: "5px" }}
      />

      <Box sx={{ display: "flex", alignItems: "center", margin: 2 }}>
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#FF4842",
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        {/* Copy Button with Tooltip */}
        <Tooltip title={isCopied ? "Copied!" : "Copy"} arrow>
          <Box
            onClick={handleCopyClick}
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {isCopied ? (
              <ContentCopyIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MultiAccordian;
